import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Mail, CheckCircle2, AlertCircle, ArrowLeft, Shield } from 'lucide-react';
import { API_BASE } from '../lib/api';

export function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email') || '';

  const [email, setEmail] = useState(emailParam);
  const [reason, setReason] = useState('user_opt_out');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (emailParam && !email) {
      setEmail(emailParam);
    }
  }, [emailParam]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const res = await fetch(`${API_BASE}/api/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), reason }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to process unsubscribe request.');
      }

      setIsSuccess(true);
    } catch (err: any) {
      setErrorMessage(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-12 px-4 sm:px-6">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-[#0A3D27] to-[#155d3d] p-6 text-white text-center">
          <div className="inline-flex p-3 bg-white/10 rounded-full mb-3 backdrop-blur-sm">
            <Mail className="w-6 h-6 text-emerald-300" />
          </div>
          <h1 className="text-2xl font-bold font-serif">Communication Preferences</h1>
          <p className="text-sm text-emerald-100/90 mt-1">
            India International Agri-Bio Expo (IIAB 2027)
          </p>
        </div>

        <div className="p-6 sm:p-8">
          {isSuccess ? (
            <div className="text-center space-y-4 py-4">
              <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h2 className="text-xl font-bold text-slate-800">You have been unsubscribed</h2>
              <p className="text-slate-600 text-sm max-w-md mx-auto">
                The address <span className="font-semibold text-slate-800">{email}</span> has been
                suppressed from all marketing and promotional communications.
              </p>
              <div className="pt-6">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0A3D27] text-white font-medium text-sm hover:bg-[#08301f] transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Return to Homepage
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <p className="text-slate-600 text-sm mb-4">
                  We are sorry to see you go. Confirm your email address below to opt out of future marketing,
                  newsletters, and promotional updates regarding IIAB 2027.
                </p>

                {errorMessage && (
                  <div className="flex items-center gap-2 p-3 mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                <label htmlFor="email" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your-email@example.com"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0A3D27] focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="reason" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Reason for Unsubscribing (Optional)
                </label>
                <select
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#0A3D27] focus:border-transparent text-sm"
                >
                  <option value="user_opt_out">I no longer wish to receive these emails</option>
                  <option value="too_frequent">Emails are too frequent</option>
                  <option value="not_relevant">Content is not relevant to me</option>
                  <option value="did_not_subscribe">I never signed up for this list</option>
                  <option value="other">Other reason</option>
                </select>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 px-4 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium text-sm transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? 'Processing...' : 'Unsubscribe from All Marketing Emails'}
                </button>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-100 text-xs text-slate-500">
                <span className="flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-emerald-700" />
                  Your preferences are stored securely (DPDP Act compliant)
                </span>
                <Link to="/" className="text-[#0A3D27] hover:underline">
                  Cancel
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default UnsubscribePage;
